import React, { useState, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { Box, IconButton, Typography } from '@mui/material';
import { Store, Extension as ExtensionIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { shopService } from '@/services';
import { LoadingSpinner } from '@/components';
import WcnTable, { DataTableColumn } from '@/components/datatable/WcnTable';
import { Shop } from '@core/types';
import { RecycleBinChildRef } from '../../RecycleBin';

const ModulesRecycleBin = forwardRef<RecycleBinChildRef>((props, ref) => {
    const { t } = useTranslation();
    const navigate = useNavigate();

    const [loading, setLoading] = useState(false);
    const [shops, setShops] = useState<Shop[]>([]);

    const loadShopsWithRecycled = useCallback(async () => {
        setLoading(true);
        try {
            const allShops = await shopService.loadAllShopsWithRecycled();
            setShops(allShops);
        } catch (error) {
            console.error('Error loading shops with recycled modules:', error);
        } finally {
            setLoading(false);
        }
    }, []);

    // Expose reload method to parent
    useImperativeHandle(ref, () => ({
        reload: () => {
            loadShopsWithRecycled();
        }
    }));

    useEffect(() => {
        loadShopsWithRecycled();
    }, [loadShopsWithRecycled]);

    const handleViewModules = (shopId: string) => {
        navigate(`/manage-shop/recyclebin/${shopId}/modules`);
    };

    const columns: DataTableColumn<Shop>[] = [
        {
            text: '',
            value: 'icon',
            width: 60,
            sortable: false,
            render: () => <Store />
        },
        {
            text: t('name'),
            value: 'name'
        },
        {
            text: '',
            value: 'actions',
            width: 100,
            align: 'right',
            sortable: false,
            render: (row) => (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <IconButton size="small" onClick={() => handleViewModules(row.id)} title={t('modules')}>
                        <ExtensionIcon fontSize="small" />
                    </IconButton>
                </Box>
            )
        }
    ];

    return (
        <Box>
            {loading && <LoadingSpinner showBackground={false} />}

            <Box sx={{ py: 3 }}>
                {!loading && shops.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography variant="h6" color="text.secondary">
                            {t('noShops')}
                        </Typography>
                    </Box>
                )}

                {!loading && shops.length > 0 && (
                    <WcnTable
                        columns={columns}
                        data={shops}
                        loading={loading}
                        noDataMessage={t('noData')}
                        size="small"
                    />
                )}
            </Box>
        </Box>
    );
});

ModulesRecycleBin.displayName = 'ModulesRecycleBin';
export default ModulesRecycleBin;
